import { isFullyAllocated, type AllocationInput } from "./allocations";

export type ReviewStatus = "needs_property" | "suggested" | "needs_split" | "reviewed";

export type PropertyOption = {
  id: string;
  name: string;
  shortName: string;
};

export type ReviewItem = {
  id: string;
  merchant: string;
  date: string;
  amountCents: number;
  category: string;
  status: ReviewStatus;
  suggestion: string;
  suggestedPropertyId?: string;
  propertyId?: string;
  propertyName?: string;
  suggestedAllocations?: AllocationInput[];
  allocations?: AllocationInput[];
  similarCount: number;
  ruleSaved?: boolean;
  note?: string;
};

export const properties: PropertyOption[] = [
  { id: "oak", name: "Oak Street", shortName: "Oak" },
  { id: "duplex", name: "Duplex", shortName: "Duplex" },
  { id: "lake", name: "Lake House", shortName: "Lake" }
];

export const initialReviewItems: ReviewItem[] = [
  {
    id: "txn-home-depot",
    merchant: "Home Depot",
    date: "Mar 4",
    amountCents: 38425,
    category: "Repairs",
    status: "needs_property",
    suggestion: "Looks like Oak Street repair",
    suggestedPropertyId: "oak",
    similarCount: 4
  },
  {
    id: "txn-city-water",
    merchant: "City Water",
    date: "Mar 9",
    amountCents: 12690,
    category: "Utilities",
    status: "suggested",
    suggestion: "Duplex utilities",
    suggestedPropertyId: "duplex",
    similarCount: 2
  },
  {
    id: "txn-insurance",
    merchant: "Insurance renewal",
    date: "Mar 12",
    amountCents: 91200,
    category: "Insurance",
    status: "needs_split",
    suggestion: "Split across 3 properties",
    suggestedAllocations: [
      { propertyId: "oak", amountCents: 38304 },
      { propertyId: "duplex", amountCents: 30096 },
      { propertyId: "lake", amountCents: 22800 }
    ],
    similarCount: 1
  }
];

export function getPropertyName(propertyId: string) {
  return properties.find((property) => property.id === propertyId)?.name ?? propertyId;
}

export function reviewStatusLabel(status: ReviewStatus) {
  const labels: Record<ReviewStatus, string> = {
    needs_property: "Needs property",
    suggested: "Ready to approve",
    needs_split: "Needs split",
    reviewed: "Reviewed"
  };

  return labels[status];
}

export function unresolvedReviewCount(items: ReviewItem[]) {
  return items.filter((item) => item.status !== "reviewed").length;
}

export function exportReadinessPercent(items: ReviewItem[]) {
  return Math.max(0, 100 - unresolvedReviewCount(items) * 3);
}

export function applySuggestedProperty(items: ReviewItem[], itemId: string) {
  return items.map((item) => {
    if (item.id !== itemId || !item.suggestedPropertyId) return item;
    const propertyName = getPropertyName(item.suggestedPropertyId);
    return {
      ...item,
      status: "reviewed" as const,
      propertyId: item.suggestedPropertyId,
      propertyName,
      note: `Assigned to ${propertyName}.`
    };
  });
}

export function applySplitPattern(items: ReviewItem[], itemId: string) {
  return items.map((item) => {
    if (item.id !== itemId || !item.suggestedAllocations) return item;

    const allocations = item.suggestedAllocations;
    const status: ReviewStatus = isFullyAllocated(item.amountCents, allocations) ? "reviewed" : "needs_split";
    return {
      ...item,
      allocations,
      status,
      note:
        status === "reviewed"
          ? `Split across ${allocations.length} properties.`
          : "Split still needs the remaining amount before export."
    };
  });
}

export function saveRuleForSimilar(items: ReviewItem[], itemId: string) {
  return items.map((item) =>
    item.id === itemId
      ? {
          ...item,
          ruleSaved: true,
          note: item.similarCount > 1 ? `Rule preview saved for ${item.similarCount} similar rows.` : item.note
        }
      : item
  );
}
